import { FormEvent, useCallback, useEffect, useState } from 'react'
import { CollectScreen } from './screens/CollectScreen'
import { FinalCheckScreen } from './screens/FinalCheckScreen'
import { SendOutScreen } from './screens/SendOutScreen'
import { ChangePasswordScreen } from './screens/ChangePasswordScreen'
import { BanknoteIcon, ClipboardCheckIcon, LogOutIcon, TruckIcon } from './components/Icons'
import { PasswordInput } from './components/PasswordInput'
import { RingPanel } from './components/RingPanel'
import {
  hasPosAccess,
  refreshSession,
  logout,
  persistCsrfForWorker,
  sessionMustChangePassword,
  clearWorkerOwner,
  PASSWORD_CHANGE_REQUIRED_EVENT,
} from './lib/auth'
import { submitOrderCash, submitFinalCheck, setReadAuthExpiredHandler } from './lib/api'
import { replayQueue } from './lib/offlineQueue'
import type { QueuedRequest } from './types'
import S from './locales/app.json'
import { LangToggle } from './components/LangToggle'
import { matchEnglish, tr, useLang, type Lang } from './lib/i18n'

type Screen = 'checking' | 'login' | 'collect' | 'denied' | 'change'

// R78 — a separate switch from Screen: Collect stays the screen that opens; this only
// toggles which panel renders once signed in. R4 adds a third panel, "Send out".
type Tab = 'collect' | 'finalCheck' | 'sendOut'

const TABS: ReadonlyArray<{ id: Tab; label: keyof typeof S; Icon: typeof BanknoteIcon }> = [
  { id: 'collect', label: 'tabCollect', Icon: BanknoteIcon },
  { id: 'finalCheck', label: 'tabFinalCheck', Icon: ClipboardCheckIcon },
  { id: 'sendOut', label: 'tabSendOut', Icon: TruckIcon },
]

// R537: the login submit handler and the replay's conflict callback store their messages in
// English, and neither is edited here (Rook #68222). The alert reads the stored English back
// to its key and draws it in the till's language; a server's own text is shown as it came.
const STORED_LOGIN = ['loginInvalid', 'loginNetworkError', 'loginLockedMinutes', 'loginLockedOneMinute', 'loginLockedLater'] as const
const STORED_CONFLICT = ['conflictFinalCheck', 'conflictCash'] as const

export function drawStored(stored: string, keys: readonly (keyof typeof S)[], lang: Lang): string {
  const hit = matchEnglish(stored, S, keys)
  return hit ? tr(S[hit.key], lang, hit.slots) : stored
}

/**
 * bench-LOCKOUT-BODY-SURFACES — distinguishes a locked-account refusal from a plain
 * credential rejection. Parses defensively: an absent or unparseable body must never
 * throw a second error into the operator's face — it falls back to status alone.
 */
async function describeLoginFailure(res: Response): Promise<{ locked: boolean; retryAfterSeconds?: number }> {
  let code: string | undefined
  let retryAfterSeconds: number | undefined
  try {
    const body = (await res.json()) as { error?: { code?: string; retryAfterSeconds?: number } }
    code = body?.error?.code
    retryAfterSeconds = body?.error?.retryAfterSeconds
  } catch {
    // absent/unparseable body — fall back to status alone
  }
  return { locked: res.status === 429 || code === 'ACCOUNT_LOCKED', retryAfterSeconds }
}

// Renders the SERVER's number, never a client-side countdown — each fresh attempt returns
// a fresh, smaller retryAfterSeconds. Degrades to a lock message without a window rather
// than showing NaN/undefined when the field is absent.
function lockedLoginMessage(retryAfterSeconds?: number): string {
  if (typeof retryAfterSeconds === 'number' && Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
    const minutes = Math.max(1, Math.round(retryAfterSeconds / 60))
    return `Account locked after repeated failed attempts. Try again in about ${minutes} minute${minutes === 1 ? '' : 's'} — retrying now will not work.`
  }
  return 'Account locked after repeated failed attempts. Try again later — retrying now will not work.'
}

// bench-CASHIER-TILL — Half B. Replaces the infra-scaffold stub with a real
// cashier surface: login gates on the existing 'pos' role (auth.ts,
// unchanged), then the collect screen. Offline replay is wired here using
// the already-built offlineQueue.replayQueue — not rebuilt.
export default function App() {
  const [screen, setScreen] = useState<Screen>('checking')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loginError, setLoginError] = useState<string | null>(null)
  const [loginSubmitting, setLoginSubmitting] = useState(false)
  const [keepSignedIn, setKeepSignedIn] = useState(false)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [conflictMessage, setConflictMessage] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('collect')
  const lang = useLang()

  // R537: the document follows the till's language: Arabic right to left.
  useEffect(() => {
    document.documentElement.lang = lang
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr'
  }, [lang])

  const checkAccess = useCallback(async () => {
    // The gateway owns the session; "am I logged in" is a question only it can answer.
    // There is no local token to inspect any more, and inventing one is what we just removed.
    const session = await refreshSession()
    if (!session.authenticated) {
      setScreen('login')
      return
    }
    // R413: a person whose password the owner set chooses their own before anything else.
    if (sessionMustChangePassword()) {
      setScreen('change')
      return
    }
    const allowed = await hasPosAccess()
    setScreen(allowed ? 'collect' : 'denied')
  }, [])

  useEffect(() => {
    void checkAccess()
  }, [checkAccess])

  const handleAuthExpired = useCallback(async () => {
    await logout()
    setScreen('login')
  }, [])

  useEffect(() => {
    setReadAuthExpiredHandler(handleAuthExpired)
    return () => setReadAuthExpiredHandler(null)
  }, [handleAuthExpired])

  // R413: a write or a replay answered 403 PASSWORD_CHANGE_REQUIRED sends the person here.
  useEffect(() => {
    const toChange = () => setScreen('change')
    window.addEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, toChange)
    return () => window.removeEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, toChange)
  }, [])

  const triggerReplay = useCallback(async () => {
    await replayQueue({
      onConflict: (action: QueuedRequest) => {
        setConflictMessage(
          action.endpoint.endsWith('/final-check')
            ? "A queued final check was refused: the order's total changed. Open Final check and approve it again."
            : `A queued cash submission (${action.endpoint}) was rejected by the server — check this order's cash record manually.`,
        )
      },
      onAuthExpired: handleAuthExpired,
      onCsrfRejected: () => {
        // Recoverable: refresh the session to re-mint the nonce and re-persist it for the
        // worker. The action stays pending and drains on the next replay.
        void refreshSession()
      },
    })
  }, [handleAuthExpired])

  useEffect(() => {
    const up = () => {
      setIsOnline(true)
      void triggerReplay()
    }
    const down = () => setIsOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [triggerReplay])

  useEffect(() => {
    if (screen === 'collect') void triggerReplay()
  }, [screen, triggerReplay])

  const handleLoginSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setLoginError(null)
    setLoginSubmitting(true)
    try {
      // R413: no stored worker owner while the session cookie may change hands.
      await clearWorkerOwner()
      // Same-origin: the gateway sets the httpOnly session cookie AND the CSRF cookie.
      // There is no token for this app to hold — that is the whole point of the migration.
      const res = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(keepSignedIn ? { username, password, trustedDevice: true } : { username, password }),
      })
      if (!res.ok) {
        const { locked, retryAfterSeconds } = await describeLoginFailure(res)
        setLoginError(locked ? lockedLoginMessage(retryAfterSeconds) : 'Invalid credentials — try again.')
        return
      }
      await refreshSession()
      await persistCsrfForWorker()
      setUsername('')
      setPassword('')
      await checkAccess()
    } catch {
      setLoginError('Network error — check your connection and try again.')
    } finally {
      setLoginSubmitting(false)
    }
  }

  const handleLogout = useCallback(async () => {
    await logout()
    setScreen('login')
  }, [])

  const handleCollectSubmit = useCallback((orderId: string, amountUsd: number) => submitOrderCash(orderId, amountUsd), [])
  const handleFinalCheckSubmit = useCallback(
    (orderId: string, expectedTotalNow: string) => submitFinalCheck(orderId, expectedTotalNow),
    [],
  )

  if (screen === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-fleet-50 text-fleet-700 font-body">
        <p className="font-condensed text-lg">{tr(S.appChecking, lang)}</p>
      </div>
    )
  }

  if (screen === 'denied') {
    return (
      <div className="relative min-h-screen flex flex-col items-center justify-center gap-4 bg-fleet-50 text-fleet-700 font-body px-6">
        {/* R537 (Rook #68276): a person stuck here in the wrong language needs the switch. */}
        <div className="absolute top-4 end-4">
          <LangToggle id="denied-lang-toggle" tone="onLight" />
        </div>
        <p className="font-condensed text-lg text-center">
          {tr(S.appDenied, lang)}
        </p>
        <button
          onClick={handleLogout}
          className="min-h-tap px-4 py-2 rounded-lg bg-fleet-700 text-white font-semibold"
        >
          {tr(S.logout, lang)}
        </button>
      </div>
    )
  }

  if (screen === 'change') {
    // R413: after a successful change the current token is refused, so sign out as a 401 does.
    // R537 (Rook #68276): the switch sits over the screen's top end corner, as on the login.
    return (
      <div className="relative">
        <div className="absolute top-4 end-4 z-10">
          <LangToggle id="change-lang-toggle" tone="onLight" />
        </div>
        <ChangePasswordScreen isOnline={isOnline} onChanged={handleAuthExpired} />
      </div>
    )
  }

  if (screen === 'login') {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-fleet-50 px-6 font-body">
        <div className="absolute top-4 end-4">
          <LangToggle id="login-lang-toggle" tone="onLight" />
        </div>
        <form onSubmit={handleLoginSubmit} className="w-full max-w-sm space-y-4">
          <h1 className="font-condensed text-2xl font-bold uppercase tracking-wide text-fleet-800 text-center mb-4">
            {tr(S.loginTitle, lang)}
          </h1>
          <div>
            <label htmlFor="login-username" className="block text-sm font-semibold text-fleet-600 mb-1.5">
              {tr(S.loginUsername, lang)}
            </label>
            <input
              id="login-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full min-h-tap rounded-lg border border-fleet-300 px-3 py-2"
            />
          </div>
          <div>
            <label htmlFor="login-password" className="block text-sm font-semibold text-fleet-600 mb-1.5">
              {tr(S.loginPassword, lang)}
            </label>
            <PasswordInput
              id="login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full min-h-tap rounded-lg border border-fleet-300 px-3 py-2"
            />
          </div>
          <label htmlFor="login-keep-signed-in" className="flex items-center gap-2 min-h-tap text-sm text-fleet-600">
            <input
              id="login-keep-signed-in"
              type="checkbox"
              checked={keepSignedIn}
              onChange={(e) => setKeepSignedIn(e.target.checked)}
              className="h-5 w-5"
            />
            <span>{tr(S.loginKeepSignedIn, lang)}</span>
          </label>
          {loginError && (
            <div role="alert" className="rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3">
              {drawStored(loginError, STORED_LOGIN, lang)}
            </div>
          )}
          <button
            type="submit"
            disabled={loginSubmitting}
            className="w-full min-h-tap rounded-lg bg-amber-500 disabled:bg-fleet-300 text-white font-semibold px-4 py-2"
          >
            {loginSubmitting ? tr(S.loginSigningIn, lang) : tr(S.loginSignIn, lang)}
          </button>
        </form>
      </div>
    )
  }

  return (
    <>
      {conflictMessage && (
        <div
          role="alert"
          className="fixed top-0 inset-x-0 z-50 bg-ember-600 text-white px-4 py-3 text-sm font-semibold flex items-center justify-between gap-4"
        >
          <span>{drawStored(conflictMessage, STORED_CONFLICT, lang)}</span>
          <button onClick={() => setConflictMessage(null)} className="flex-shrink-0 underline min-h-tap px-2">
            {tr(S.dismiss, lang)}
          </button>
        </div>
      )}
      {/* ux3 (a): one header on every tab - title, online/offline pill, Log out. The pill
          reads the same navigator online/offline events Collect's banner already used, and
          Log out is the same handleLogout, only moved here from Collect. */}
      <header id="app-header" className="sticky top-0 z-40 bg-navy text-white">
        <div className="w-full max-w-sm mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <span id="app-title" className="text-lg font-semibold">
            {tr(S.appTitle, lang)}
          </span>
          <div className="flex items-center gap-2">
            <span
              id="online-pill"
              role="status"
              className={
                isOnline
                  ? 'inline-flex min-h-tap items-center gap-2 rounded-full bg-white/15 text-white text-sm font-semibold px-3'
                  : 'inline-flex min-h-tap items-center gap-2 rounded-full bg-ember-100 text-ember-700 text-sm font-semibold px-3'
              }
            >
              <span
                aria-hidden="true"
                className={isOnline ? 'h-2.5 w-2.5 rounded-full bg-jade-400' : 'h-2.5 w-2.5 rounded-full bg-ember-500'}
              />
              {isOnline ? tr(S.online, lang) : tr(S.offline, lang)}
            </span>
            <LangToggle id="header-lang-toggle" tone="onNavy" />
            <button
              id="header-logout-btn"
              type="button"
              onClick={handleLogout}
              className="min-h-tap inline-flex items-center gap-1.5 rounded-xl border border-white/40 px-3 text-sm font-semibold text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <LogOutIcon className="h-4 w-4 shrink-0" />
              <span>{tr(S.logout, lang)}</span>
            </button>
          </div>
        </div>
      </header>
      {/* ux3 (b): tabs as icon + label; the active tab is navy and underlined. */}
      <nav aria-label={tr(S.tabsLabel, lang)} className="w-full bg-white border-b border-fleet-200">
        <div className="w-full max-w-sm mx-auto flex">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              data-tab={id}
              aria-pressed={tab === id}
              onClick={() => setTab(id)}
              className={
                tab === id
                  ? 'flex-1 min-h-tap inline-flex flex-col items-center justify-center gap-1 border-b-4 border-navy text-navy text-sm font-semibold px-2 pt-2 pb-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-navy'
                  : 'flex-1 min-h-tap inline-flex flex-col items-center justify-center gap-1 border-b-4 border-transparent text-fleet-400 text-sm font-semibold px-2 pt-2 pb-1.5 hover:text-fleet-600 hover:bg-fleet-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-navy'
              }
            >
              <Icon className="h-6 w-6 shrink-0" />
              <span>{tr(S[label], lang)}</span>
            </button>
          ))}
        </div>
      </nav>
      {/* R503 (c): the till ring sits above every tab, so an order waiting at final check rings
          whichever tab is open. */}
      <RingPanel screen="till" />
      {tab === 'collect' && <CollectScreen isOnline={isOnline} onSubmit={handleCollectSubmit} />}
      {tab === 'finalCheck' && <FinalCheckScreen onCheck={handleFinalCheckSubmit} />}
      {tab === 'sendOut' && <SendOutScreen />}
    </>
  )
}
